import '@/app/ui/style.css';
import { Auth0Provider } from '@auth0/auth0-react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import * as React from 'react';
import ReactDOM from 'react-dom/client';
import AppProvider from '@/app/appContext';
import { ErrorComponent } from '@/app/ui/components/error';
import { NotFound } from '@/app/ui/components/not-found';
import { AnchoredToastProvider, ToastProvider, toastManager } from '@/app/ui/components/toast';
import { TooltipProvider } from '@/app/ui/components/tooltip';
import { Auth0Exporter, auth0Exports, useAuth } from '@/app/utils/auth';
import { routeTree } from './routeTree.gen';
import { CSPostHogProvider, posthog } from '@/app/components/posthog/PosthogProvider';
import { initSentry } from '@/app/utils/sentry';
import { IntlWrapper } from '@/app/i18n/provider';
import { Spinner } from '@/app/ui/components/spinner';

let isLoggingOut = false;

const onQueryError = (error: Error) => {
  if ((error as AxiosError).response?.status === 401) {
    if (isLoggingOut) return;
    isLoggingOut = true;

    const callbackUrl = `/login?target=${encodeURIComponent(window.location.pathname)}`;

    const logout = auth0Exports.getLogout();
    if (logout) {
      logout(window.location.origin + callbackUrl);
    } else {
      window.location.href = callbackUrl;
    }
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 300 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    },
    mutations: {
      onError: (error) => {
        toastManager.add({
          type: 'error',
          title: 'An error occurred',
          description: error.message,
        });
      },
    },
  },
  queryCache: new QueryCache({ onError: onQueryError }),
  mutationCache: new MutationCache({ onError: onQueryError }),
});

const router = createRouter({
  routeTree,
  scrollRestoration: true,
  getScrollRestorationKey: (location) => location.pathname,
  context: { queryClient, auth: undefined! },
  defaultPreload: 'intent',
  defaultPendingMs: 150,
  defaultPendingMinMs: 200,
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFound,
  defaultPendingComponent: () => <Spinner kind="inset" />,
  defaultErrorComponent: ({ error }) => (
    <ErrorComponent
      className="pt-20 md:pt-40"
      error={{
        name: error.name,
        message: error.message,
      }}
    />
  ),
  Wrap: ({ children }) => (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: import.meta.env.VITE_AUTH0_REDIRECT_URI,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      }}
      skipRedirectCallback
      cacheLocation="localstorage"
      useRefreshTokens
    >
      <QueryClientProvider client={queryClient}>
        <CSPostHogProvider>
          <IntlWrapper>
            <AppProvider>
              <Auth0Exporter />
              <ToastProvider>
                <AnchoredToastProvider>
                  <TooltipProvider delay={250}>{children}</TooltipProvider>
                </AnchoredToastProvider>
              </ToastProvider>
            </AppProvider>
          </IntlWrapper>
        </CSPostHogProvider>
      </QueryClientProvider>
    </Auth0Provider>
  ),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

initSentry(router);

if (typeof window !== 'undefined' && posthog) {
  router.subscribe('onLoad', ({ toLocation }) => {
    let url = window.origin + toLocation.pathname;
    if (toLocation.searchStr) {
      url = url + toLocation.searchStr;
    }
    posthog.capture('$pageview', { $current_url: url });
  });
}

const rootElement = document.getElementById('app')!;
if (!rootElement.innerHTML || rootElement.innerHTML.trim().length === 0) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function App() {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ auth, queryClient }} />;
}
